import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'

interface MyVendorGroupsProps {
  userId: string
  userType: 'broadcaster' | 'ad-creator'
}

interface VendorGroup {
  id: string
  name: string
  description: string | null
  created_at: string
  group_type: string
}

export default function MyVendorGroups({ userId, userType }: MyVendorGroupsProps) {
  // Determine which group_type to fetch based on user type
  const groupType = userType === 'broadcaster' ? 'broadcaster' : 'ad-creator'

  const { data: groups, isLoading } = useQuery({
    queryKey: ['my-vendor-groups', userId, userType],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('groups')
          .select(`
            id,
            name,
            description,
            created_at,
            group_type
          `)
          .eq('owner_id', userId)
          .eq('group_type', groupType)
          .order('created_at', { ascending: false })

        if (error) {
          console.warn('Error fetching vendor groups:', error)
          return []
        }
        return (data || []) as VendorGroup[]
      } catch (err) {
        console.warn('Error fetching vendor groups:', err)
        return []
      }
    },
    enabled: !!userId,
  })

  if (isLoading) {
    return (
      <div 
        className="rounded-lg p-6 border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
        }}
      >
        <p style={{ color: 'var(--text-primary)' }}>Loading vendor groups...</p>
      </div>
    )
  }

  return (
    <div 
      className="rounded-lg p-6 border-2"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--bg-tertiary)',
        color: 'var(--text-primary)',
      }}
    >
      <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
        My Vendor Groups
      </h2>

      {!groups || groups.length === 0 ? (
        <p style={{ color: 'var(--text-primary)' }}>
          You haven't created any vendor groups yet.
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const createdDate = format(new Date(group.created_at), 'dd/MM/yyyy')

            return (
              <div
                key={group.id}
                className="border-b-2 pb-4 last:border-b-0 last:pb-0"
                style={{ borderColor: 'var(--bg-tertiary)' }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Link
                      to={`/groups/${group.id}`}
                      className="block"
                    >
                      <h3 
                        className="font-semibold text-lg mb-1 hover:underline"
                        style={{ color: 'var(--text-link)' }}
                      >
                        {group.name}
                      </h3>
                    </Link>
                    {group.description && (
                      <p 
                        className="text-sm mb-2"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {group.description}
                      </p>
                    )}
                    <p 
                      className="text-sm"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      Created: {createdDate}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

